/**
 * Data templates for each chart type.
 * getTemplate(chartId) → { columns, rows, instructions, editTip, columnCount }
 * columns: string[] — ordered column names
 * rows: object[] — example data rows
 * instructions: string — what to upload (shown on import screen)
 * editTip: string — feature hints for the table editor
 * columnCount: number — how many columns this chart typically needs
 */

const TEMPLATES = {
  'vertical-bar': {
    columns: ['Model', 'Accuracy (%)'],
    rows: [
      { Model: 'BERT-base', 'Accuracy (%)': 85.2 },
      { Model: 'RoBERTa', 'Accuracy (%)': 89.1 },
      { Model: 'DeBERTa', 'Accuracy (%)': 91.5 },
      { Model: 'ALBERT', 'Accuracy (%)': 82.3 },
      { Model: 'DistilBERT', 'Accuracy (%)': 78.8 },
    ],
    instructions: 'Upload a CSV with labels and scores. We\'ll auto-detect columns and handle messy formatting like $, %, or commas.',
    editTip: 'Values have $, %, or commas? Use the ▾ menu on any column header to strip them in one click.',
    columnCount: 2,
  },
  'horizontal-bar': {
    columns: ['Model', 'Latency (ms)'],
    rows: [
      { Model: 'BERT-base', 'Latency (ms)': 45.2 },
      { Model: 'RoBERTa', 'Latency (ms)': 52.1 },
      { Model: 'DeBERTa', 'Latency (ms)': 61.5 },
      { Model: 'ALBERT', 'Latency (ms)': 38.3 },
      { Model: 'DistilBERT', 'Latency (ms)': 22.8 },
    ],
    instructions: 'Upload labels and values — we\'ll auto-detect the layout. Extra columns from your export? You can remove them here.',
    editTip: 'Too many columns? Use the ▾ menu on any column header to remove ones you don\'t need.',
    columnCount: 2,
  },
  'lollipop': {
    columns: ['Model', 'F1 Score'],
    rows: [
      { Model: 'BERT-base', 'F1 Score': 88.2 },
      { Model: 'RoBERTa', 'F1 Score': 90.1 },
      { Model: 'DeBERTa', 'F1 Score': 92.5 },
      { Model: 'ALBERT', 'F1 Score': 85.3 },
      { Model: 'DistilBERT', 'F1 Score': 82.8 },
    ],
    instructions: 'Upload labels and scores. Column order doesn\'t matter — we\'ll figure out which is which.',
    editTip: 'Columns in the wrong order? Drag any column header to rearrange.',
    columnCount: 2,
  },
  'diverging-bar': {
    columns: ['Method', 'Δ Accuracy (%)'],
    rows: [
      { Method: 'Data Augmentation', 'Δ Accuracy (%)': 4.5 },
      { Method: 'Label Smoothing', 'Δ Accuracy (%)': 2.3 },
      { Method: 'Mixup', 'Δ Accuracy (%)': 1.1 },
      { Method: 'Dropout 0.3', 'Δ Accuracy (%)': 0.2 },
      { Method: 'Weight Decay', 'Δ Accuracy (%)': -0.4 },
      { Method: 'Smaller Batch', 'Δ Accuracy (%)': -1.8 },
      { Method: 'No Warmup', 'Δ Accuracy (%)': -3.1 },
      { Method: 'FP16 Only', 'Δ Accuracy (%)': -5.3 },
    ],
    instructions: 'Upload labels with positive and negative values (e.g. ablation deltas). We\'ll split them left and right automatically.',
    editTip: 'Long method names? The chart will auto-truncate labels on the axis — hover to see the full name.',
    columnCount: 2,
  },
  'grouped-bar': {
    columns: ['Model', 'MMLU', 'GPQA', 'BBH'],
    rows: [
      { Model: 'GPT-4o', MMLU: 88, GPQA: 53, BBH: 83 },
      { Model: 'Claude 3.5', MMLU: 89, GPQA: 59, BBH: 86 },
      { Model: 'Gemini Pro', MMLU: 84, GPQA: 46, BBH: 78 },
      { Model: 'LLaMA 3 70B', MMLU: 82, GPQA: 41, BBH: 75 },
    ],
    instructions: 'Upload your benchmark table as-is — one row per model, one column per metric. We\'ll detect the grouping automatically.',
    editTip: 'We\'ll ask you to choose between "wide" (one column per metric) and "long" (metric name in a column) on the next step.',
    columnCount: 3,
  },
  'horizontal-grouped-bar': {
    columns: ['Model', 'MMLU', 'GPQA', 'BBH'],
    rows: [
      { Model: 'GPT-4o', MMLU: 88, GPQA: 53, BBH: 83 },
      { Model: 'Claude 3.5', MMLU: 89, GPQA: 59, BBH: 86 },
      { Model: 'Gemini Pro', MMLU: 84, GPQA: 46, BBH: 78 },
      { Model: 'LLaMA 3 70B', MMLU: 82, GPQA: 41, BBH: 75 },
    ],
    instructions: 'Upload your benchmark table as-is — one row per model, one column per metric. We\'ll render metrics as grouped horizontal bars.',
    editTip: 'Use horizontal grouping when category names are too long for a bottom axis.',
    columnCount: 3,
  },
  'stacked-bar': {
    columns: ['Dataset', 'Correct', 'Partially Correct', 'Incorrect'],
    rows: [
      { Dataset: 'SQuAD', Correct: 72, 'Partially Correct': 18, Incorrect: 10 },
      { Dataset: 'TriviaQA', Correct: 65, 'Partially Correct': 22, Incorrect: 13 },
      { Dataset: 'NQ', Correct: 58, 'Partially Correct': 25, Incorrect: 17 },
      { Dataset: 'HotpotQA', Correct: 51, 'Partially Correct': 28, Incorrect: 21 },
    ],
    instructions: 'Upload a breakdown table — one row per category, one column per segment. We\'ll detect duplicates and warn you if entries overlap.',
    editTip: 'Ran the same benchmark twice? We\'ll flag duplicate entries on the next step so nothing gets silently merged.',
    columnCount: 3,
  },
  'horizontal-stacked-bar': {
    columns: ['Dataset', 'Correct', 'Partially Correct', 'Incorrect'],
    rows: [
      { Dataset: 'SQuAD', Correct: 72, 'Partially Correct': 18, Incorrect: 10 },
      { Dataset: 'TriviaQA', Correct: 65, 'Partially Correct': 22, Incorrect: 13 },
      { Dataset: 'NQ', Correct: 58, 'Partially Correct': 25, Incorrect: 17 },
      { Dataset: 'HotpotQA', Correct: 51, 'Partially Correct': 28, Incorrect: 21 },
    ],
    instructions: 'Upload a breakdown table — one row per category, one column per segment. We\'ll render the composition as horizontal stacks.',
    editTip: 'Use horizontal stacks when labels need more space on the left axis.',
    columnCount: 3,
  },
  'heatmap': {
    columns: ['Predicted', 'Actual', 'Count'],
    rows: [
      { Predicted: 'Positive', Actual: 'Positive', Count: 142 },
      { Predicted: 'Positive', Actual: 'Negative', Count: 18 },
      { Predicted: 'Positive', Actual: 'Neutral', Count: 7 },
      { Predicted: 'Negative', Actual: 'Positive', Count: 12 },
      { Predicted: 'Negative', Actual: 'Negative', Count: 138 },
      { Predicted: 'Negative', Actual: 'Neutral', Count: 15 },
      { Predicted: 'Neutral', Actual: 'Positive', Count: 5 },
      { Predicted: 'Neutral', Actual: 'Negative', Count: 9 },
      { Predicted: 'Neutral', Actual: 'Neutral', Count: 124 },
    ],
    instructions: 'Upload a confusion matrix, correlation table, or any grid. Both wide (matrix) and long (3-column) formats work — we auto-detect and pivot.',
    editTip: 'Have a matrix with row labels? Just upload it directly — we\'ll auto-pivot it into heatmap format for you.',
    columnCount: 3,
  },
  'combo': {
    columns: ['Epoch', 'Samples Processed', 'Val Loss'],
    rows: [
      { Epoch: 1, 'Samples Processed': 50000, 'Val Loss': 2.11 },
      { Epoch: 2, 'Samples Processed': 100000, 'Val Loss': 1.73 },
      { Epoch: 3, 'Samples Processed': 150000, 'Val Loss': 1.42 },
      { Epoch: 4, 'Samples Processed': 200000, 'Val Loss': 1.28 },
      { Epoch: 5, 'Samples Processed': 250000, 'Val Loss': 1.16 },
      { Epoch: 6, 'Samples Processed': 300000, 'Val Loss': 1.09 },
    ],
    instructions: 'Upload a table with a label column and two numeric columns — one will be bars (left axis), the other a line (right axis).',
    editTip: 'European decimals (commas instead of dots)? We\'ll detect them and offer a one-click convert.',
    columnCount: 3,
  },
  'radar': {
    columns: ['Model', 'Speed', 'Accuracy', 'Recall', 'Precision', 'F1'],
    rows: [
      { Model: 'BERT-base', Speed: 85, Accuracy: 72, Recall: 78, Precision: 80, F1: 79 },
      { Model: 'DeBERTa', Speed: 60, Accuracy: 91, Recall: 88, Precision: 90, F1: 89 },
    ],
    instructions: 'Upload one row per model — each numeric column becomes an axis on the radar. Great for benchmark comparisons.',
    editTip: 'Each numeric column becomes a radar axis. Use the ▾ menu to remove any metrics you don\'t want to compare.',
    columnCount: 4,
  },
  'line-chart': {
    columns: ['Epoch', 'Train Loss', 'Val Loss'],
    rows: [
      { Epoch: 1, 'Train Loss': 1.82, 'Val Loss': 2.11 },
      { Epoch: 2, 'Train Loss': 1.34, 'Val Loss': 1.73 },
      { Epoch: 3, 'Train Loss': 1.01, 'Val Loss': 1.42 },
      { Epoch: 4, 'Train Loss': 0.79, 'Val Loss': 1.28 },
      { Epoch: 5, 'Train Loss': 0.62, 'Val Loss': 1.16 },
    ],
    instructions: 'Paste or upload training curves directly. Multiple loss columns? Each becomes a separate line automatically.',
    editTip: 'Multiple value columns will each become a line. On the next step you\'ll pick which columns to plot.',
    columnCount: 2,
  },
  'scatter-plot': {
    columns: ['Parameters (M)', 'Accuracy (%)', 'Model Family'],
    rows: [
      { 'Parameters (M)': 110, 'Accuracy (%)': 82.1, 'Model Family': 'BERT' },
      { 'Parameters (M)': 125, 'Accuracy (%)': 84.6, 'Model Family': 'BERT' },
      { 'Parameters (M)': 340, 'Accuracy (%)': 88.9, 'Model Family': 'BERT' },
      { 'Parameters (M)': 355, 'Accuracy (%)': 90.2, 'Model Family': 'BERT' },
      { 'Parameters (M)': 7000, 'Accuracy (%)': 73.8, 'Model Family': 'LLaMA' },
      { 'Parameters (M)': 13000, 'Accuracy (%)': 79.4, 'Model Family': 'LLaMA' },
      { 'Parameters (M)': 33000, 'Accuracy (%)': 83.1, 'Model Family': 'LLaMA' },
      { 'Parameters (M)': 65000, 'Accuracy (%)': 86.0, 'Model Family': 'LLaMA' },
    ],
    instructions: 'Upload two numeric columns for X and Y. Add a text column to color-code points by model family or cluster (optional).',
    editTip: 'Have a text column for grouping (e.g. model family)? We\'ll auto-detect it and color-code your points.',
    columnCount: 2,
  },
  'histogram': {
    columns: ['Inference Latency (ms)'],
    rows: [
      { 'Inference Latency (ms)': 23.5 },
      { 'Inference Latency (ms)': 31.2 },
      { 'Inference Latency (ms)': 28.7 },
      { 'Inference Latency (ms)': 35.1 },
      { 'Inference Latency (ms)': 42.8 },
      { 'Inference Latency (ms)': 38.4 },
      { 'Inference Latency (ms)': 29.6 },
      { 'Inference Latency (ms)': 44.2 },
      { 'Inference Latency (ms)': 33.9 },
      { 'Inference Latency (ms)': 26.1 },
      { 'Inference Latency (ms)': 37.5 },
      { 'Inference Latency (ms)': 41.3 },
    ],
    instructions: 'Upload any CSV with numeric data. Extra columns? Remove them with the ▾ menu. Empty rows from Excel are trimmed automatically.',
    editTip: 'Only one numeric column is needed — we\'ll bin the values. Use the ▾ menu to remove any columns you don\'t need.',
    columnCount: 1,
  },
  'box-plot': {
    columns: ['Model', 'Inference Time (ms)'],
    rows: [
      { Model: 'BERT-base', 'Inference Time (ms)': 45 },
      { Model: 'BERT-base', 'Inference Time (ms)': 42 },
      { Model: 'BERT-base', 'Inference Time (ms)': 51 },
      { Model: 'BERT-base', 'Inference Time (ms)': 48 },
      { Model: 'BERT-base', 'Inference Time (ms)': 44 },
      { Model: 'DistilBERT', 'Inference Time (ms)': 22 },
      { Model: 'DistilBERT', 'Inference Time (ms)': 25 },
      { Model: 'DistilBERT', 'Inference Time (ms)': 19 },
      { Model: 'DistilBERT', 'Inference Time (ms)': 24 },
      { Model: 'DistilBERT', 'Inference Time (ms)': 21 },
    ],
    instructions: 'Upload repeated measurements grouped by model or condition. Missing values? We\'ll exclude them and tell you how many were skipped.',
    editTip: 'Rows with blank values are automatically excluded from the chart. You\'ll see a count above the preview.',
    columnCount: 2,
  },
};

const DEFAULT_TEMPLATE = TEMPLATES['vertical-bar'];

export function getTemplate(chartId) {
  return TEMPLATES[chartId] || DEFAULT_TEMPLATE;
}

/**
 * Convert a template (or any { columns, rows }) to a 2D string grid.
 * grid[0] = header row, grid[1..] = data rows (all strings).
 */
export function templateToGrid(template) {
  const { columns, rows } = template;
  const dataRows = rows.map(row => columns.map(col => String(row[col] ?? '')));
  return [columns, ...dataRows];
}

/**
 * Create an empty grid with numCols columns and numRows data rows.
 */
export function emptyGrid(numCols = 3, numRows = 5) {
  const headers = Array.from({ length: numCols }, (_, i) => `Column ${i + 1}`);
  const data = Array.from({ length: numRows }, () => Array(numCols).fill(''));
  return [headers, ...data];
}

export default TEMPLATES;
